import {
  CreateRecipeInput,
  Recipe as RecipeGraphql,
  UpdateRecipeInput,
} from '@akin-home/amplify';
import { filterHasValue, notNull } from '@akin-home/arrays';
import {
  getOptionIDorNull,
  optionGraphqlToModelOrUndefined,
} from '@akin-home/options';
import {
  BudgetRatingID,
  createRecipe,
  HealthRatingID,
  preparationNoteGraphqlToModel,
  Recipe,
} from '@akin-home/recipes';
import { getId } from '@akin-home/records';

export * from './createRecipeFromModel';
export * from './getPrepNoteIcon';
export * from './getRecipeFilterFromMealPreferences';
export * from './getRecipeFilterOptionsFromUserPreferences';
export * from './updateRecipeFromModel';

export const recipeGraphqlToModel = (rec: RecipeGraphql): Recipe => {
  const { image, ...recipe } = rec;

  const specialDietM2mItems = filterHasValue(recipe.specialDiets?.items);

  const specialDiets = filterHasValue(
    specialDietM2mItems.map((i) => optionGraphqlToModelOrUndefined(i?.option))
  );

  return createRecipe({
    ...recipe,
    id: recipe.id,
    habitatID: notNull(recipe.habitatID),
    description: notNull(recipe.description),
    inProgress: notNull(recipe.inProgress),
    health: notNull(recipe.health as HealthRatingID),
    budget: notNull(recipe.budget as BudgetRatingID),
    default_servings: notNull(recipe.default_servings),
    name: recipe.name,
    emoji: recipe.emoji || '',
    imageUrl: recipe.imageUrl || undefined,
    image: image || undefined,
    imageCache: image || undefined,
    mainIngredient: optionGraphqlToModelOrUndefined(recipe.mainIngredient),
    dishType: optionGraphqlToModelOrUndefined(recipe.dishType),
    skillLevel: optionGraphqlToModelOrUndefined(recipe.skillLevel),
    servingSize: optionGraphqlToModelOrUndefined(recipe.servingSize),
    cuisineType: optionGraphqlToModelOrUndefined(recipe.cuisineType),
    prepTime: optionGraphqlToModelOrUndefined(recipe.prepTime),
    familyfriendlyType: optionGraphqlToModelOrUndefined(
      recipe.familyfriendlyType
    ),
    textureType: optionGraphqlToModelOrUndefined(recipe.textureType),
    specialDietM2mItems,
    specialDiets,
    specialDietIDs: filterHasValue(recipe.specialDietIDs),
    ingredients: filterHasValue(recipe.ingredients) as any,
    allergyIDs: filterHasValue(recipe.allergyIDs),

    prepNotes: preparationNoteGraphqlToModel(filterHasValue(recipe.prepNotes)),
    utensils: filterHasValue(recipe.utensils),
    mealTypes: filterHasValue(recipe.mealTypes),
    readGroups: filterHasValue(recipe.readGroups),
  });
};

export const recipeModelToCreateRecipeInput = (
  recipe: Recipe
): CreateRecipeInput => {
  const {
    image,
    mainIngredient,
    skillLevel,
    servingSize,
    cuisineType,
    familyfriendlyType,
    textureType,
    dishType,
    specialDiets,
    specialDietIDs,
    prepTime,
  } = recipe;

  return {
    // ...recipeInput,
    name: recipe.name,
    readGroups: recipe.readGroups,
    description: recipe.description,
    emoji: recipe.emoji,
    inProgress: recipe.inProgress,
    mealTypes: recipe.mealTypes,
    health: recipe.health || null,
    budget: recipe.budget || null,
    utensils: recipe.utensils || null,
    allergyIDs: recipe.allergyIDs || null,
    habitatID: recipe.habitatID || null,
    default_servings: recipe.default_servings || null,
    mainIngredientID: getOptionIDorNull(mainIngredient),
    skillLevelID: getOptionIDorNull(skillLevel),
    servingSizeID: getOptionIDorNull(servingSize),
    cuisineTypeID: getOptionIDorNull(cuisineType),
    familyfriendlyTypeID: getOptionIDorNull(familyfriendlyType),
    textureTypeID: getOptionIDorNull(textureType),
    dishTypeID: getOptionIDorNull(dishType),
    prepTimeID: getOptionIDorNull(prepTime),
    imageUrl: recipe.imageUrl || null,
    imageID: image?.id || null,
    specialDietIDs:
      specialDietIDs || (specialDiets && specialDiets.map(getId)) || null,
    prepNotes: recipe.prepNotes as any,
    ingredients: recipe.ingredients.length ? recipe.ingredients : null,
  };
};

export const recipeModelToUpdateRecipeInput = (
  recipe: Recipe
): UpdateRecipeInput => ({
  ...recipeModelToCreateRecipeInput(recipe),
  id: recipe.id,
});
